import type {
  AdminEvaluation,
  AdminEvaluationReport,
  Discussion,
  FinetuneTarget,
  Finetunes,
  MemoryAnswer,
  MemoryCompany,
  MemoryGraph,
  MemorySummary,
  MemoryCypherResult,
  ReferenceRoom,
  AdminUser,
  Artefact,
  AuthProviders,
  BillingCatalogue,
  CreditState,
  CurrentUser,
  AuditLevel,
  AuditPage,
  CallDetails,
  MemoryQueryPage,
  NewPlaybook,
  DataRoomSummary,
  DeckOutline,
  EngagementInput,
  Finding,
  Manifest,
  ObservedSource,
  Options,
  Playbook,
  Project,
  RunEvent,
  ProjectSummary,
  Proposal,
  MailPage,
  RoomKind,
  TagResult,
  UploadItem,
  RunSummary,
  Score,
  UploadResult,
  SessionState,
  LinkCheck,
  LinkRedeemed,
  LinkRefusal,
  ToolServer,
  RunTask,
  Ceilings,
} from "./types";

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** Artefact names can contain `/` (`images/foo.png`); the route is `{name:path}`. */
function encodePath(name: string): string {
  return name.split("/").map(encodeURIComponent).join("/");
}

/**
 * The CSRF token, read from the cookie the server issued.
 *
 * Deliberately readable here: echoing it in a header is the half an attacker
 * on another origin cannot do, which is what makes the double submit work.
 * The session cookie beside it stays httponly.
 */
function csrfToken(): string {
  const match = document.cookie.match(/(?:^|;\s*)ca-csrf=([^;]*)/);
  return match?.[1] ? decodeURIComponent(match[1]) : "";
}

async function get<T>(path: string): Promise<T> {
  const response = await fetch(path, { headers: { Accept: "application/json" } });
  if (!response.ok) {
    throw new ApiError(await errorMessage(response), response.status);
  }
  return response.json() as Promise<T>;
}

async function send<T>(
  method: "POST" | "PATCH" | "PUT" | "DELETE",
  path: string,
  body?: unknown,
): Promise<T> {
  const response = await fetch(path, {
    method,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-CSRF-Token": csrfToken(),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!response.ok) {
    throw new ApiError(await errorMessage(response), response.status);
  }
  return response.status === 204 ? (undefined as T) : (response.json() as Promise<T>);
}

function post<T>(path: string, body?: unknown): Promise<T> {
  return send<T>("POST", path, body);
}

/** The `detail` FastAPI puts a refusal in, or `fallback` if this is not that. */
function detailIn(body: string, fallback: string): string {
  try {
    const data = JSON.parse(body) as { detail?: unknown };
    if (typeof data.detail === "string") return data.detail;
    if (data.detail) return JSON.stringify(data.detail);
  } catch {
    /* not JSON — fall through to the status text */
  }
  return fallback;
}

/**
 * One of the three sign-in link calls, with the token in the body — a URL
 * would put it in an access log.
 *
 * A refusal comes back as a value rather than a throw: "used", "expired" and
 * "invalid" are states the sign-in page draws, each with its own way on, and
 * it branches on the `code` rather than the prose.
 */
async function linkCall<T>(
  action: "peek" | "redeem" | "resend",
  token: string,
): Promise<T | LinkRefusal> {
  const response = await fetch(`/api/auth/email/${action}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ token }),
  });
  if (response.status === 400) {
    const body = (await response
      .clone()
      .json()
      .catch(() => null)) as Partial<LinkRefusal> | null;
    if (body && typeof body.code === "string") return body as LinkRefusal;
  }
  if (!response.ok) {
    throw new ApiError(await errorMessage(response), response.status);
  }
  return response.json() as Promise<T>;
}

async function errorMessage(response: Response): Promise<string> {
  const body = await response.text().catch(() => "");
  return detailIn(body, response.statusText || `HTTP ${response.status}`);
}

export const api = {
  health: () => get<{ status: string; version: string }>("/api/health"),
  /** What `/api/health` used to say. Behind the gate — a run id names a client. */
  status: () =>
    get<{
      status: string;
      version: string;
      runs_root: string;
      live_runs: string[];
      queued_runs: string[];
      max_concurrent: number;
      /**
       * Whether the knowledge graph can be written to. A graph a schema
       * version behind the code opens read-only, so uploads are stored,
       * converted and silently not indexed — which is invisible unless
       * somebody says it.
       */
      memory: {
        expected_schema: number;
        reachable: boolean;
        writable: boolean;
        schema?: number;
        migration_required?: boolean;
      };
    }>("/api/status"),

  dataRooms: (kind?: RoomKind) =>
    get<DataRoomSummary[]>(
      kind ? `/api/data-rooms?kind=${encodeURIComponent(kind)}` : "/api/data-rooms",
    ),
  createDataRoom: (name: string, kind: RoomKind = "data_room") =>
    post<DataRoomSummary>("/api/data-rooms", { name, kind }),
  deleteDataRoom: (id: string) =>
    send<void>("DELETE", `/api/data-rooms/${encodeURIComponent(id)}`),

  /**
   * Ask the agent what the room, and each untagged file in it, is about.
   *
   * A call the client makes rather than something an upload does: uploading
   * has always been free and this spends money. It labels only the untagged,
   * so pressing it again after an upload costs one batch for the new files
   * and nothing for the old ones.
   */
  tagDataRoom: (id: string) =>
    post<TagResult>(`/api/data-rooms/${encodeURIComponent(id)}/tags`, {}),

  /**
   * Describe the call an interview room holds — the expert, the network, the
   * terms. It applies to transcripts uploaded *after* it, because the graph's
   * `:Interview` is written as a file is ingested.
   */
  describeCall: (id: string, call: CallDetails) =>
    send<DataRoomSummary & { refused: string[] }>(
      "PUT",
      `/api/data-rooms/${encodeURIComponent(id)}/call`,
      call,
    ),

  /**
   * Multipart, so it cannot go through `send` — that sets a JSON content type,
   * and letting the browser set its own is what produces the boundary. The
   * CSRF header still has to be sent by hand for the same reason.
   *
   * **`XMLHttpRequest` rather than `fetch`, for `onProgress` alone.** `fetch`
   * can report a *download* as it arrives and has nothing to say about an
   * upload until it is over, which for a data room is the whole of the wait:
   * a folder of CIMs is minutes of sending and a few seconds of answer. The
   * older API is the only one in a browser that reports bytes leaving.
   *
   * `onProgress` is given a fraction of *this* request, not a count of bytes,
   * because a multipart body is the files plus boundaries and headers — a
   * caller totalling `File.size` would watch the bar overshoot its own total.
   */
  uploadToDataRoom: (
    id: string,
    files: UploadItem[],
    password?: string,
    onProgress?: (fraction: number) => void,
  ) =>
    new Promise<UploadResult>((resolve, reject) => {
      const form = new FormData();
      // The third argument is the *filename the server sees*, and it is what
      // carries a folder drop's shape across the wire — `form.append(k, file)`
      // sends `file.name`, which is the basename and nothing else.
      for (const item of files) form.append("files", item.file, item.path);
      // A form field, not a query parameter: it stays out of the access log and
      // out of the browser's history. It is used server-side to convert the
      // document once and is stored nowhere.
      if (password) form.append("password", password);

      const request = new XMLHttpRequest();
      request.open("POST", `/api/data-rooms/${encodeURIComponent(id)}/files`);
      request.setRequestHeader("X-CSRF-Token", csrfToken());
      if (onProgress) {
        request.upload.addEventListener("progress", (event) => {
          // A body of unknown length reports no total, and dividing by it
          // would put the bar at `Infinity`.
          if (event.lengthComputable && event.total > 0) {
            onProgress(event.loaded / event.total);
          }
        });
      }
      request.addEventListener("load", () => {
        if (request.status >= 200 && request.status < 300) {
          resolve(JSON.parse(request.responseText) as UploadResult);
          return;
        }
        reject(
          new ApiError(
            detailIn(
              request.responseText,
              request.statusText || `HTTP ${request.status}`,
            ),
            request.status,
          ),
        );
      });
      // A dropped connection, and a cancelled one. Both are silent otherwise:
      // the promise would never settle and the page would wait for ever.
      request.addEventListener("error", () =>
        reject(new ApiError("The upload could not reach the server.", 0)),
      );
      request.addEventListener("abort", () =>
        reject(new ApiError("The upload was stopped.", 0)),
      );
      request.send(form);
    }),

  /**
   * A password for a file already uploaded — it arrives in a second mail, an
   * hour later, and re-uploading a sixty-megabyte CIM to supply it is not a
   * reasonable ask.
   */
  unlockDataRoomFile: (id: string, filename: string, password: string) =>
    post<UploadResult>(
      `/api/data-rooms/${encodeURIComponent(id)}/files/unlock`,
      { filename, password },
    ),

  adminEvents: (params: {
    q?: string;
    kind?: string;
    level?: AuditLevel;
    errors?: boolean;
    limit?: number;
    offset?: number;
  }) => {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== "" && value !== false) {
        search.set(key, String(value));
      }
    }
    return get<AuditPage>(`/api/admin/events?${search.toString()}`);
  },

  /**
   * What the agent asked the knowledge graph, and what came back.
   *
   * A separate facility from the trail rather than a filter on it: the Cypher,
   * its parameters and a slice of the result are three fields nothing else
   * has, at a volume nothing else reaches.
   */
  adminMemoryQueries: (params: {
    q?: string;
    mode?: "read" | "write";
    run_id?: string;
    errors?: boolean;
    limit?: number;
    offset?: number;
  }) => {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== "" && value !== false) {
        search.set(key, String(value));
      }
    }
    return get<MemoryQueryPage>(`/api/admin/memory-queries?${search.toString()}`);
  },

  /** Every figure type, for the panel that offers them. */
  createPlaybook: (body: NewPlaybook) =>
    post<Playbook>("/api/playbooks", body),

  credits: () => get<CreditState>("/api/credits"),

  /** Every depth's ceiling for a playbook on a choice of models. Empty fields
   *  mean the defaults, as they do to `start`. */
  ceilings: (params: {
    playbook: string;
    provider: string;
    model: string;
    subagentModel: string;
    prompted: boolean;
  }) => {
    const search = new URLSearchParams({
      playbook: params.playbook,
      provider: params.provider,
      model: params.model,
      subagent_model: params.subagentModel,
      prompted: String(params.prompted),
    });
    return get<Ceilings>(`/api/ceilings?${search.toString()}`);
  },

  reportIssue: (body: { text: string; page: string }) =>
    post<{ recorded: boolean; emailed: number }>("/api/issues", body),
  billingPackages: () => get<BillingCatalogue>("/api/billing/packages"),
  /** Returns Stripe's hosted checkout URL; the caller navigates to it. */
  checkout: (packageId: string) =>
    post<{ url: string; id: string }>("/api/billing/checkout", {
      package_id: packageId,
    }),

  discussion: (id: string) =>
    get<Discussion>(`/api/runs/${encodeURIComponent(id)}/discussion`),
  askDiscussion: (id: string, question: string) =>
    send<Discussion>("POST", `/api/runs/${encodeURIComponent(id)}/discussion`, {
      question,
    }),
  clearDiscussion: (id: string) =>
    send<void>("DELETE", `/api/runs/${encodeURIComponent(id)}/discussion`),

  adminMemorySchema: () =>
    get<{ schema: string }>("/api/admin/memory/schema"),
  adminMemoryAsk: (question: string) =>
    send<MemoryAnswer>("POST", "/api/admin/memory/ask", { question }),
  /** The company network and corroboration the Memory page draws. Free. */
  memoryGraph: () => get<MemoryGraph>("/api/memory/graph"),
  /** One company and what it is joined to, or the names `name` fits. Free. */
  memoryCompany: (name: string) =>
    get<MemoryCompany>(`/api/memory/graph/company?name=${encodeURIComponent(name)}`),
  /** A briefing written from memory, as the person asking may see it (#135). */
  memorySummary: (question: string) =>
    send<MemorySummary>("POST", "/api/memory/summary", { question }),
  /** No `cypher` means "translate this question", which runs nothing. */
  adminMemoryCypher: (body: { question?: string; cypher?: string }) =>
    send<MemoryCypherResult>("POST", "/api/admin/memory/cypher", body),

  adminEvaluations: () => get<AdminEvaluation[]>("/api/admin/evaluations"),
  adminEvaluationReferences: () =>
    get<ReferenceRoom[]>("/api/admin/evaluations/references"),
  adminNoteImprovement: (runId: string, improvement: string) =>
    send<AdminEvaluation>(
      "PUT",
      `/api/admin/evaluations/${encodeURIComponent(runId)}/improvement`,
      { improvement },
    ),
  adminEvaluation: (runId: string) =>
    get<AdminEvaluationReport>(
      `/api/admin/evaluations/${encodeURIComponent(runId)}`,
    ),
  adminStartEvaluation: (body: {
    room_id: string;
    filename: string;
    target: string;
    playbook: string;
    sector?: string;
    geography?: string;
  }) => send<{ run_id: string; reference: string }>(
    "POST",
    "/api/admin/evaluations",
    body,
  ),

  /**
   * Grade a run that has already finished. No engagement is started and
   * nothing is charged but the comparison — the run's own playbook, target and
   * inputs are whatever they were, which is why none of them are sent.
   */
  adminEvaluateExisting: (body: {
    run_id: string;
    room_id: string;
    filename: string;
  }) => send<{ run_id: string; reference: string; playbook: string }>(
    "POST",
    "/api/admin/evaluations/existing",
    body,
  ),

  adminUsers: () => get<AdminUser[]>("/api/admin/users"),
  /** `notified`: whether a person just let in was emailed to say so. */
  adminSetStatus: (id: string, status: string) =>
    send<AdminUser & { notified: boolean }>("PUT", `/api/admin/users/${encodeURIComponent(id)}/status`, {
      status,
    }),
  adminSetRole: (id: string, role: string) =>
    send<AdminUser>("PUT", `/api/admin/users/${encodeURIComponent(id)}/role`, {
      role,
    }),
  adminGrant: (id: string, amountUsd: number, note?: string) =>
    post<{ id: string }>(`/api/admin/users/${encodeURIComponent(id)}/credit`, {
      amount_usd: amountUsd,
      note: note ?? null,
    }),

  /**
   * Ask for an emailed sign-in link.
   *
   * The answer is deliberately the same whether or not the address has an
   * account — anything else would make this a way to enumerate who is under
   * review here — so there is nothing for the caller to branch on.
   */
  requestLoginLink: (email: string, playbook?: string | null) =>
    post<{ status: string; detail: string }>("/api/auth/email/request", {
      email,
      playbook: playbook ?? null,
    }),

  /** Where a link stands, without spending it — what `/signin` asks first. */
  checkLoginLink: (token: string) => linkCall<LinkCheck>("peek", token),
  /** Spend the link: the button, never the opening of the page. */
  redeemLoginLink: (token: string) => linkCall<LinkRedeemed>("redeem", token),
  /** A new link to the address an old one was for. 429 says when to retry. */
  resendLoginLink: (token: string) =>
    linkCall<{ status: string; email: string; detail: string }>("resend", token),

  session: () => get<SessionState>("/api/auth/session"),
  authProviders: () => get<AuthProviders>("/api/auth/providers"),
  me: () => get<CurrentUser>("/api/me"),

  /**
   * Accept the beta terms.
   *
   * Sends no version: accepting is accepting what the server is showing now,
   * and a client-supplied one would let a stale tab record consent to wording
   * nobody is being shown any more.
   */
  acceptDisclaimer: () => send<CurrentUser>("PUT", "/api/me/disclaimer"),
  logout: () => post<{ status: string }>("/api/auth/logout"),
  playbooks: () => get<Playbook[]>("/api/playbooks"),
  options: () => get<Options>("/api/options"),
  tools: () => get<ToolServer[]>("/api/tools"),

  runs: () => get<RunSummary[]>("/api/runs"),
  run: (id: string) => get<Manifest>(`/api/runs/${encodeURIComponent(id)}`),
  findings: (id: string) =>
    get<Finding[]>(`/api/runs/${encodeURIComponent(id)}/findings`),
  sources: (id: string) =>
    get<ObservedSource[]>(`/api/runs/${encodeURIComponent(id)}/sources`),
  artefacts: (id: string) =>
    get<Artefact[]>(`/api/runs/${encodeURIComponent(id)}/artefacts`),
  artefact: (id: string, name: string) =>
    get<{ name: string; content: string }>(
      `/api/runs/${encodeURIComponent(id)}/artefacts/${encodePath(name)}`,
    ),

  /**
   * A deck as text, one entry per slide.
   *
   * There is no browser renderer for PowerPoint that does not mean shipping
   * one, so the server reads the titles and body text out with python-pptx and
   * the client shows the outline.
   */
  /**
   * Write the board summary, in the background like a deck (#129). Returns
   * the task at once; the page watches it on the run and selects
   * `summary.md` when it lands. `force` writes it afresh — without it a
   * summary already on disk is returned for nothing.
   */
  writeSummary: (id: string, force = false) =>
    send<RunTask>("POST", `/api/runs/${encodeURIComponent(id)}/summary`, { force }),

  /**
   * Build a deck from one markdown deliverable. Returns immediately: the work
   * runs in the background and the run's `task` field is where it is watched.
   */
  buildDeck: (id: string, name: string) =>
    send<RunTask>("POST", `/api/runs/${encodeURIComponent(id)}/deck`, { name }),

  /** Every finetune of this run, and what became of each. */
  finetunes: (id: string) =>
    get<Finetunes>(`/api/runs/${encodeURIComponent(id)}/finetune`),

  /**
   * Change one part of a deliverable. Returns immediately, like a deck: the
   * work runs in the background, the run's `task` is where it is watched and
   * its events are what the panel shows.
   */
  finetune: (id: string, name: string, target: FinetuneTarget, request: string) =>
    send<RunTask>("POST", `/api/runs/${encodeURIComponent(id)}/finetune`, {
      name,
      kind: target.kind,
      line: target.line,
      label: target.label,
      request,
    }),

  undoFinetune: (id: string, requestId: string) =>
    send<Finetunes>("POST", `/api/runs/${encodeURIComponent(id)}/finetune/undo`, {
      id: requestId,
    }),

  /** Render the run's .html and .docx again from their markdown. Free. */
  republish: (id: string) =>
    send<{ notes: string[] }>("POST", `/api/runs/${encodeURIComponent(id)}/republish`),

  /**
   * The app's own page for reading one HTML deliverable — the file at full
   * width with the Finetune panel beside it. "Open in new tab" goes here: a
   * file opened on its own has nobody to send a request through.
   */
  readerUrl: (id: string, name: string) =>
    `/engagements/${encodeURIComponent(id)}/read/${encodePath(name)}`,

  deckOutline: (id: string, name: string) =>
    get<DeckOutline>(
      `/api/runs/${encodeURIComponent(id)}/outline/${encodePath(name)}`,
    ),

  /** The raw file URL. Used as an iframe `src` and as a download target. */
  fileUrl: (id: string, name: string, download = false) =>
    `/api/runs/${encodeURIComponent(id)}/files/${encodePath(name)}` +
    (download ? "?download=1" : ""),

  /**
   * A link one deliverable writes to another, as a URL this app can follow.
   *
   * Deliverables are published side by side in one workspace, so a reference
   * between them is a bare filename — `cim_review.html`. That is right in the
   * file, right in the HTML preview (the frame is served from `/files/`, so
   * the browser resolves it against the same folder) and wrong in the markdown
   * tab, where it would resolve against `/runs/<id>` and land on the SPA's
   * catch-all instead: a reference that reads as a link and goes nowhere.
   *
   * Anything already addressed — a scheme, a protocol-relative host, an
   * absolute path, a fragment — is left exactly as written.
   */
  siblingUrl: (id: string, href: string) => {
    const bare =
      !/^[a-z][a-z0-9+.-]*:/i.test(href) &&
      !href.startsWith("//") &&
      !href.startsWith("/") &&
      !href.startsWith("#");
    if (!bare) return href;
    // The fragment is the browser's, not the path's. Handed to `fileUrl`
    // whole it comes back percent-encoded into the filename, and the link
    // 404s on a file whose name contains a `#` — a reference that worked as
    // plain text, broken by being made into a link.
    const cut = href.indexOf("#");
    return cut === -1
      ? api.fileUrl(id, href)
      : api.fileUrl(id, href.slice(0, cut)) + href.slice(cut);
  },

  /** An artefact's bytes. The docx preview needs an ArrayBuffer, not text. */
  fileBytes: async (id: string, name: string) => {
    const response = await fetch(
      `/api/runs/${encodeURIComponent(id)}/files/${encodePath(name)}`,
    );
    if (!response.ok) {
      throw new ApiError(await errorMessage(response), response.status);
    }
    return response.arrayBuffer();
  },

  adminMail: (params: { proposal?: string; direction?: string; outcome?: string; before?: string }) => {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) if (value) query.set(key, value);
    const search = query.toString();
    return get<MailPage>(`/api/admin/mail${search ? `?${search}` : ""}`);
  },
  proposal: (id: string) =>
    get<Proposal>(`/api/mail/proposals/${encodeURIComponent(id)}`),
  orderProposal: (id: string, option: number) =>
    post<{ run_id: string }>(
      `/api/mail/proposals/${encodeURIComponent(id)}/order`,
      { option },
    ),

  start: (body: Partial<EngagementInput>) =>
    post<{ run_id: string; status: string; target: string; playbook: string }>(
      "/api/engagements",
      body,
    ),
  resume: (id: string, maxCostUsd?: number | null) =>
    post<{ run_id: string }>(
      `/api/runs/${encodeURIComponent(id)}/resume`,
      { max_cost_usd: maxCostUsd ?? null },
    ),
  cancel: (id: string) =>
    post<{ run_id: string; status: string }>(
      `/api/runs/${encodeURIComponent(id)}/cancel`,
    ),

  eventLog: (id: string, since: number) =>
    get<{ total: number; events: RunEvent[] }>(
      `/api/runs/${encodeURIComponent(id)}/log?since=${since}`,
    ),
  eventsUrl: (id: string) => `/api/runs/${encodeURIComponent(id)}/events`,

  projects: (params?: {
    status?: string;
    client?: string;
    project_type?: string;
    search?: string;
  }) => {
    const query = new URLSearchParams(
      Object.entries(params ?? {}).filter(([, v]) => v) as [string, string][],
    ).toString();
    return get<Project[]>(`/api/projects${query ? `?${query}` : ""}`);
  },
  projectSummary: () => get<ProjectSummary>("/api/projects/summary"),
  project: (id: number) => get<Project>(`/api/projects/${id}`),
  createProject: (body: Partial<Project>) => post<Project>("/api/projects", body),
  updateProject: (id: number, body: Partial<Project>) =>
    send<Project>("PATCH", `/api/projects/${id}`, body),
  scoreProject: (id: number, score: Score | null, note?: string, by?: string) =>
    send<Project>("PUT", `/api/projects/${id}/score`, { score, note, by }),
  deleteProject: (id: number) => send<void>("DELETE", `/api/projects/${id}`),
};
